import { Injectable } from '@angular/core';
import { UserModel } from './user.model';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import {
    ILightWeightObject,
    ILightWeightObjectCollection,
    ILightWeightUser,
    UserParameterModel,
} from '@fi-shared/models';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiApplicationLanguageType } from '../shared/fi-content-language-shared/fi-application-language-type';
import { isDefined } from '@fi-shared/utils/is-defined';
import { FiLocaleService } from '@fi-shared/fi-locale.service';
import { FiMobileAppHelperService } from '../utilities/fi-services/mobile-app-helper.service';
import { Observable, Subject } from 'rxjs';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { UserForSupportSession } from '@fi-app/components/logged-in-user/user-for-support-session.iterface';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { IItemAvailability } from '@fi-shared/models/common';
import { AppTextService } from '@fi-shared/app-text';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { UnitColleagueParameterModel } from '@fi-app/components/colleagues-list/unit-colleague-parameter-model';
import { IUserModel } from '@fi-shared/models/user.model';
import { FiUserViewDialogComponent } from '@fi-app/user/fi-user-view-dialog/fi-user-view-dialog.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IFiHttpPromise } from '@fi-shared/to-fi-promise';
import { FiCountryHelperService } from '@fi-shared/fi-contacts/fi-countries-select/country-helper.service';
import { ILightWeightTag } from '@fi-shared/fi-tag2/fi-tag2';
import { ObjectTypes } from '@fi-shared/types';
import { IUnitColleagueModel } from '@fi-app/components/colleagues-list/colleagues-list';
import { IUserListItemModel, IUserViewDialogData } from '@fi-app/user/user';
import { DataFieldModel } from '@fi-app/data-field/data-field.model';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { DataFieldService } from '@fi-app/data-field/data-field.service';

@Injectable()
export class UserService {
    private currentApplicationType: FiApplicationLanguageType;

    private _userSaved$ = new Subject<UserModel>();
    get userSaved$() {
        return this._userSaved$.asObservable();
    }

    constructor(
        private fiBackend: FiBackendService,
        private appTextService: AppTextService,
        private countryHelperService: FiCountryHelperService,
        private localeService: FiLocaleService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private fiStateChangeService: FiStateChangeService,
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private fiDialogsService: FiDialogsService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private stateAccessCheckService: StateAccessCheckService,
        private dataFieldService: DataFieldService,
        private loadingService: LoadingService,
    ) {
        setTimeout(() => {
            this.fiStateChangeService.onSuccess$.subscribe(() => {
                if (
                    this.currentApplicationType &&
                    this.currentApplicationType !== this.userDataService.applicationLanguage
                ) {
                    this.updateApplicationLanguage(this.currentApplicationType);
                }
            });
        });
    }

    public updateApplicationLanguage(applicationLanguage: FiApplicationLanguageType) {
        const promise = this.appTextService.loadApplicationLanguageLabels(applicationLanguage);
        promise.then(() => {
            this.localeService.setLanguage(applicationLanguage);
            if (this.userDataService.userData) {
                this.userDataService.setApplicationLanguage(applicationLanguage);
            }
            // should reload country codes
            this.countryHelperService.refreshCountryCodes();

            this.mobileAppHelperService.applicationLanguageChanged(applicationLanguage);
        });
        return promise;
    }

    getUserForEdit(id: number, isAccessRequesting: boolean): Promise<UserModel> {
        return new Promise<UserModel>((resolve, reject) => {
            const user = new UserModel(this.customerDataService.customerData);
            let url = `Core/User/GetUserForEdit/${id}`;
            if (isDefined(isAccessRequesting)) {
                url += `?isAccessRequesting=${isAccessRequesting}`;
            }

            this.fiBackend.fetchData<IUserModel>(url).then((response) => {
                user.Initialize(response);
                user.InitializeContacts(response, user);
                user.InitializeAddresses(response, user);
                if (user.Id == this.userDataService.getUserId()) {
                    this.currentApplicationType = user.ApplicationLanguageType;
                }
                resolve(user);
            }, reject);
        });
    }

    getUserForView(id: number): Promise<UserModel> {
        return new Promise<UserModel>((resolve, reject) => {
            const user = new UserModel(this.customerDataService.customerData);
            this.fiBackend.fetchData<IUserModel>(`Core/User/GetUserForView/${id}`).then((response) => {
                user.Initialize(response);
                user.InitializeContacts(response, user);
                user.InitializeAddresses(response, user);
                resolve(user);
            }, reject);
        });
    }

    getNewUserInstance() {
        const user = new UserModel(this.customerDataService.customerData);
        user.checkAddDefaults(this.userDataService.applicationLanguage);
        user.ApplicationLanguageType = undefined;
        return Promise.resolve(user);
    }

    getUsersListData(parameter: UserParameterModel) {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IUserListItemModel>>(
            'Core/User/GetUsersListData',
            parameter,
        );
    }

    getUsersForSupportSession() {
        return this.fiBackend.fetchData<UserForSupportSession[]>('Core/User/GetUsersForSupportSession');
    }

    getUnitColleaguesListData(
        parameter: UnitColleagueParameterModel,
    ): IFiHttpPromise<IItemsListDataFetchResult<IUnitColleagueModel>> {
        return this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IUnitColleagueModel>>(
            'Core/User/GetUnitColleaguesListData',
            parameter,
        );
    }

    saveUser(userToSave: UserModel) {
        //Creating a new object because it looks bad in the GUI otherwise.
        var promise = this.fiBackend.fetchDataPost<UserModel>('Core/User/SaveUser', userToSave);
        promise.then((user) => {
            if (user.Id == this.userDataService.getUserId()) {
                this.currentApplicationType = user.ApplicationLanguageType;
            }
            this._userSaved$.next(user);
        });
        return promise;
    }

    deleteUsers(userIdsToDelete: number[]) {
        return this.loadingService.trackPromise(this.fiBackend.fetchDataPost('Core/User/DeleteUsers', userIdsToDelete));
    }

    GetLightWeightUsers() {
        return this.fiBackend.fetchData<Array<ILightWeightUser>>('Core/User/GetUsersForSharing');
    }

    checkUserAvailable(userId: number) {
        // throws exception if item is not available
        return this.fiBackend.fetchData<IItemAvailability>('Core/User/IsUserAvailable/' + userId);
    }

    getUserTagsByUserAccessRights(): Promise<ILightWeightTag[]> {
        return this.fiBackend.fetchData<ILightWeightTag[]>(
            `Core/Tag/GetModuleTagsByUserAccessRights/${ModuleType.Core}`,
        );
    }

    getLightWeightUsers(userIds: number[]) {
        return this.fiBackend.fetchDataPost<ILightWeightUser[]>('Core/User/GetLightWeightUsers', userIds);
    }

    getUserTags() {
        return this.fiBackend.fetchData<ILightWeightObject[]>('Core/User/GetUserTags');
    }

    getUsersWithModuleAccess(moduleType, accessRightType) {
        return this.fiBackend.fetchData<Array<ILightWeightUser>>(
            'Core/User/GetUsersWithModuleAccess/{0}/{1}'.format(moduleType, accessRightType),
        );
    }

    private prepareUserViewDialogData(userId: number): Promise<IUserViewDialogData> {
        const data: IUserViewDialogData = {};
        const resultPromise = new Promise<IUserViewDialogData>((resolve, reject) => {
            Promise.all([
                this.getUserForView(userId).then((user: UserModel) => {
                    data.user = user;
                    data.hasEditAccess =
                        this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.User.Edit, {
                            Id: user.Id,
                        }) && user.HasEditAccess;
                }),
                this.dataFieldService.getUserFields(userId).then((userDataFields: DataFieldModel[]) => {
                    data.userDataFields = userDataFields;
                }),
            ]).then(() => resolve(data), reject);
        });
        return this.loadingService.trackPromise(resultPromise);
    }

    openUserViewDialog(userId: number): void {
        this.prepareUserViewDialogData(userId).then((userViewDialogData) => {
            this.fiDialogsService
                .custom({
                    contentComponentRef: FiUserViewDialogComponent,
                    contentComponentData: userViewDialogData,
                    hideHeader: true,
                    hideFooter: true,
                    hideDialogTopBorder: true,
                    config: {
                        //panelClass: 'fi-item-view-dialog-panel',
                        width: '844px',
                        maxWidth: 'unset',
                    },
                })
                .afterClosed((result) => {
                    if (result.value?.userModel) {
                        if (userId === this.userDataService.getUserId()) {
                            this.showCantDeleteCurrentUserDialog();
                            return;
                        }
                        this.fiItemsDeleteHelperService.userDeleteConfirm([result.value.userModel]).then(() => {
                            this.loadingService.trackPromise(
                                this.deleteUsers([userId]).then(() => {
                                    this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                                        ObjectTypes.User,
                                        [result.value.userModel.Id],
                                        result.value.userModel.FullName,
                                    );
                                    this.removeUserFromList$.next(userId);
                                }),
                            );
                        });
                    }
                });
        });
    }

    private removeUserFromList$ = new Subject<number>();
    get onRemoveUserFromList$(): Observable<number> {
        return this.removeUserFromList$.asObservable();
    }

    showCantDeleteCurrentUserDialog() {
        this.fiDialogsService.error({
            headerText: this.appTextService.get('common.oops'),
            messageText: this.appTextService.get('user.delete_current_user_warning') + ' 🙂',
        });
    }
}
